import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import type { FileProgress, MediaItemDetailed } from "../types";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

// Tables name are defined in `defaultConfig` file,
// since we have multiple config source, and one the configs come from remote server,
// defining all table names is not feasible, so it should accept `defaultConfig` table name and any other
type ApiEndpointsTableName =
  | "media"
  | "family-areas"
  | "places"
  | "trip"
  | "special-things"
  | "simple-tags"
  | "persons"
  | "partners"
  | "collections"
  | "languages"
  | "plant-animals"
  | "groups"
  // NOTE: since ts compiler will narrow down type for (ApiEndpointsTableName | string) to string,
  // but it should  also accept any table name as long as value is string.
  // Needed a hack to preserve autocomplete
  | (string & {});

interface MediaState {
  items: MediaItemDetailed[] | undefined;
  selectedItem?: MediaItemDetailed;
  tableType?: ApiEndpointsTableName;
  mode?: "upload" | "edit";

  uploadItems?: MediaItemDetailed[];
  uploadItemsProgress?: Record<string, FileProgress>;

  editItems: MediaItemDetailed[] | undefined;

  /**
   * Layer B popup: whether the MediaDrawer carousel (MediaEntityDetailModal) is open.
   * This is INDEPENDENT of the primary popup (Layer A / popupStore).
   * A geo-entity or non-geo-entity popup can be open at the same time as the
   * media detail carousel — they never block each other.
   */
  isMediaDetailOpen: boolean;

  // Separate namespace for store actions
  actions: {
    setMediaItems: (payload: MediaState["items"]) => void;
    setSelectedItem: (payload: MediaState["selectedItem"]) => void;
    setUploadItems: (payload: MediaState["uploadItems"]) => void;
    setUploadItemProgress: (payload: MediaState["uploadItemsProgress"]) => void;
    setCommonMediaState: (payload: Partial<Omit<MediaState, "actions">>) => void;

    setEditItems: (payload: MediaState["editItems"]) => void;
    clearEditItems: () => void;

    /**
     * Open the media detail carousel overlay (Layer B).
     * Sets isMediaDetailOpen=true and updates selectedItem.
     * Does NOT affect the primary popup (Layer A / popupStore).
     */
    openMediaDetail: (item: MediaItemDetailed) => void;
    /**
     * Close the media detail carousel overlay (Layer B).
     * Sets isMediaDetailOpen=false and clears selectedItem.
     */
    closeMediaDetail: () => void;

    resetCommonMediaState: () => void;
  };
}

const actionName = actionNameBuilder("media");

export const useMediaStore = create<MediaState>()(
  devtools(
    immer(
      applyResetMiddleware((set, _, store) => ({
        items: [],
        selectedItem: undefined,
        mode: undefined,
        uploadItems: [],
        uploadItemsProgress: undefined,
        editItems: undefined,
        isMediaDetailOpen: false,

        actions: {
          setMediaItems: (payload) => set(() => ({ items: payload }), undefined, actionName("setMediaItems")),
          setSelectedItem: (payload) => set(() => ({ selectedItem: payload }), undefined, actionName("setSelectedItem")),
          setUploadItems: (payload) => set(() => ({ uploadItems: payload }), undefined, actionName("setUploadItems")),
          setUploadItemProgress: (payload) => set(() => ({ uploadItemsProgress: payload }), undefined, actionName("setUploadItemProgress")),
          setCommonMediaState: (payload) =>
            set((state) => ({ ...payload, actions: state.actions }), undefined, actionName("setCommonMediaState")),

          setEditItems: (editItems) => set(() => ({ editItems }), undefined, actionName("setEditItems")),
          clearEditItems: () => set(() => ({ editItems: undefined })),

          openMediaDetail: (item) =>
            set(
              (state) => {
                state.isMediaDetailOpen = true;
                state.selectedItem = item;
              },
              undefined,
              actionName("openMediaDetail")
            ),

          closeMediaDetail: () =>
            set(
              (state) => {
                state.isMediaDetailOpen = false;
                state.selectedItem = undefined;
              },
              undefined,
              actionName("closeMediaDetail")
            ),

          resetCommonMediaState: () => set(store.getInitialState(), undefined, actionName("resetCommonMediaState"))
        }
      }))
    ),
    {
      name: "mediaStore"
    }
  )
);

// Hook for store states
export const useMediaSelector = () =>
  useMediaStore(
    useShallow((state) => ({
      items: state.items,
      selectedItem: state.selectedItem,
      mode: state.mode,
      uploadItems: state.uploadItems,
      uploadItemsProgress: state.uploadItemsProgress,
      editItems: state.editItems,
      isMediaDetailOpen: state.isMediaDetailOpen,
    }))
  );

// Hook for store actions
export const useMediaActions = () => useMediaStore((state) => state.actions);
